import { useSelector } from 'react-redux';

import type {
  CompletePersonProfile,
  PersonProfile,
  PersonRegistrationData,
} from '@appTypes/esb';
import { routeParamsSel } from '@modules/location/duck/selectorsTyped';
import {
  useGetCompletePersonProfileQuery,
  useGetPersonProfileQuery,
} from '@modules/rtk/esb-api';
import type { Program } from '@shared/constants';
import { programForUnitTypeId, unitTypeIdForUnitType } from '@shared/constants';
import { getCurrentRegistration } from '@shared/hooks/usePersonActiveRegistration';

interface YouthDetailsRouteParams {
  orgGuid?: string;
  /** Scoutbook user id (`sb_user_id` from the roster report, same as the ESB userId). */
  userId?: string;
}

interface UseYouthProfileResult {
  profile?: PersonProfile;
  akelaProfile?: CompletePersonProfile;
  /** The program of the unit the youth is being viewed in. */
  activeProgram?: Program;
  loading: boolean;
  registration?: PersonRegistrationData;
  registrationLoading: boolean;
  isError: boolean;
}

/**
 * Fetches the youth profile for the youth-details route
 * (`/unit-roster/:orgGuid/:userId`) in the shape `YouthProfileCard` takes.
 *
 * The `userId`-keyed profile carries everything the card shows except the
 * registration status, which only exists on the `personGuid`-keyed complete
 * profile. That second request is chained on the `personGuid` the first one
 * returns, so it is reported separately and the card can render the rest of the
 * youth without waiting for it.
 */
export function useYouthProfile(): UseYouthProfileResult {
  const { orgGuid, userId } = useSelector(
    routeParamsSel<YouthDetailsRouteParams>,
  );
  const numericUserId = Number(userId);

  const {
    data: profile,
    isFetching: loading,
    isError: isErrorProfile,
  } = useGetPersonProfileQuery(
    { userId: numericUserId },
    { skip: !numericUserId || Number.isNaN(numericUserId) },
  );

  const personGuid = profile?.profile?.personGuid;

  const {
    data: akelaProfile,
    isFetching: isFetchingComplete,
    isError: isErrorComplete,
  } = useGetCompletePersonProfileQuery(
    { personGuid: personGuid as string },
    { skip: !personGuid },
  );

  // The unit the roster was opened from, so a youth in several units is shown in
  // the colors of the one the viewer is looking at
  const units = profile?.organizationPositions ?? [];
  const unit = units.find(u => u.organizationGuid === orgGuid) ?? units[0];

  return {
    profile,
    akelaProfile,
    activeProgram: unit
      ? programForUnitTypeId[unitTypeIdForUnitType[unit.unitType]]
      : undefined,
    loading,
    registration: getCurrentRegistration(
      akelaProfile?.registrarInfo ?? [],
      orgGuid,
    ),
    // Also true in the render where the personGuid is known but the chained
    // query has not started yet, so the pill does not flash empty first.
    registrationLoading: isFetchingComplete || (!!personGuid && !akelaProfile),
    isError: isErrorProfile || isErrorComplete,
  };
}
